import { PERMISSIONS } from '@shared/constants/permissions';

/**
 * What each employee record area IS — its URL segment, its label and the key
 * the backend requires to read it — without the component that renders it.
 *
 * Kept apart from `record-areas.ts` so the route table can declare one guarded
 * route per area without pulling every tab's code into the entry bundle; the
 * profile joins the two. `viewPermission` is the key the BACKEND requires to
 * read the area, so the route guard and the tab that leads to it agree by
 * construction. Areas with no entry are covered by the route's own
 * `employees.view` guard, which is what their controllers require.
 */
export interface EmployeeRecordAreaMeta {
  /** URL segment under `/employees/:employeeId/`. */
  segment: string;
  /** Tab label and page title. */
  title: string;
  /** One line describing what the area holds. */
  description: string;
  /** Permission required to read it, when the area publishes its own key. */
  viewPermission?: string;
}

export const EMPLOYEE_RECORD_AREA_META = {
  documents: {
    segment: 'documents',
    title: 'Documents',
    description: 'Identity, education and employment documents on file.',
    viewPermission: PERMISSIONS.employeeDocuments.view,
  },
  /**
   * Addresses — the same `AddressesStep` the wizard mounts, over the same
   * `employee_addresses` links the profile's Personal Information card reads.
   *
   * It exists because View Profile could DISPLAY an address and not change one:
   * the only editor was inside the Edit Employee wizard. No `viewPermission` —
   * there is no list route, the records arrive on the employee detail payload
   * the profile route's `employees.view` guard already covers, and the write
   * routes require `employees.update`, which the step checks.
   */
  addresses: {
    segment: 'addresses',
    title: 'Address',
    description: 'Residential and permanent addresses on record.',
  },
  education: {
    segment: 'education',
    title: 'Education',
    description: 'Qualifications and academic history.',
  },
  'emergency-contacts': {
    segment: 'emergency-contacts',
    title: 'Emergency Contacts',
    description: 'Who to reach, and how, in an emergency.',
  },
  'past-employment': {
    segment: 'past-employment',
    title: 'Past Employment',
    description: 'Previous employers and the documents supporting them.',
  },
  skills: {
    segment: 'skills',
    title: 'Skills',
    description: 'Assigned skills and experience levels.',
    viewPermission: PERMISSIONS.employeeSkills.view,
  },
  /**
   * Bank & Statutory — the same `BankStatutorySection` the Edit Employee wizard
   * mounts, so the two surfaces cannot drift.
   *
   * It exists as a tab because View Profile could not show an employee's bank
   * details at all: the only surface was inside the edit wizard, and an
   * employee's accounts are part of their record, not a step of a form.
   *
   * No `viewPermission` — `GET /employees/:id/bank-accounts` is gated on
   * `employees.view`, which the profile route already requires, and account
   * NUMBERS arrive masked regardless. Reading one in full is
   * `employees.view_sensitive` on its own route, checked inside the component,
   * and the write actions check `employees.update`. That is what lets
   * HR_ASSISTANT administer an employee's banking without being able to read a
   * number back — and it grants nothing at all on COMPANY bank accounts, which
   * are a different resource behind `company-bank-accounts.view`.
   */
  'bank-statutory': {
    segment: 'bank-statutory',
    title: 'Bank & Statutory',
    description: 'Bank accounts, statutory identifiers and the documents evidencing them.',
  },
  /**
   * Salary — the employee's COMPENSATION STRUCTURE, and how it has changed.
   *
   * Backed entirely by the existing compensation subresource
   * (`/employees/:id/compensations`, `/compensations/effective`, `/…/revise`)
   * and its adjustments sibling. It defines what the employee is paid; Payroll
   * processes it, and runs, payments, transactions and payslips stay there.
   *
   * `viewPermission` is `compensation.view` — the key those routes actually
   * require. It was split out of `employees.view` so that administering an
   * employee's record and seeing what that person is paid stopped being the
   * same right, and salary visibility follows that key alone: the roles holding
   * it today are OWNER, CO_FOUNDER, HR_OPERATIONS_LEAD, HR and HR_ASSISTANT.
   * For every role without it — including DIRECTOR and every manager and
   * individual contributor — this tab is neither listed nor reachable by URL,
   * the employee payload arrives without `salary` or `current_compensation`,
   * and the compensation routes refuse a direct call. The write actions inside
   * check `compensation.manage`. No role is named in the code: which roles hold
   * the key is the backend matrix's business, and this list changes with it.
   */
  salary: {
    segment: 'salary',
    title: 'Salary',
    description: 'Compensation structure, revisions and salary history.',
    viewPermission: PERMISSIONS.compensation.view,
  },
  /**
   * Career & Lifecycle is a READ of records that already exist — the employee's
   * own joining, confirmation and resignation dates, and the separation history
   * the Employee Exit module holds. It stores nothing of its own and creates no
   * event; a stage the system does not record simply does not appear.
   *
   * No `viewPermission`: the employment dates come from the employee detail
   * response the route's own `employees.view` guard already covers, and the
   * separation read is gated inside the component on `employee-exits.view` — so
   * a caller without that key still sees the dates rather than an empty tab.
   */
  'career-lifecycle': {
    segment: 'career-lifecycle',
    title: 'Career & Lifecycle',
    description: 'Recorded employment milestones and separation history.',
  },
  /**
   * History — every recorded change to this employee and their records.
   *
   * No `viewPermission`: `GET /employees/:id/history` is gated on
   * `employees.view`, which the route already requires, and the backend filters
   * each ENTRY by the right that governs the data it describes. A caller
   * without `compensation.view` gets the same timeline without the salary
   * entries rather than a tab they cannot open — which is the honest answer,
   * because they can see the employee, just not what the employee is paid.
   */
  history: {
    segment: 'history',
    title: 'History',
    description: 'Who changed what, and when.',
  },
} as const satisfies Record<string, EmployeeRecordAreaMeta>;

export type EmployeeRecordAreaKey = keyof typeof EMPLOYEE_RECORD_AREA_META;

/** Alias kept for the profile, which mounts exactly these areas as its tabs. */
export type EmployeeProfileTabAreaKey = EmployeeRecordAreaKey;

/**
 * The profile's record tabs, in tab order.
 *
 * Listed explicitly rather than derived from the object, so the tab bar's order
 * is readable here and stays independent of key ordering.
 */
export const EMPLOYEE_PROFILE_TAB_AREA_KEYS = [
  'documents',
  'addresses',
  'education',
  'emergency-contacts',
  'past-employment',
  'skills',
  'bank-statutory',
  'salary',
  'career-lifecycle',
  'history',
] as const satisfies readonly EmployeeProfileTabAreaKey[];
